
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth";
import { ExtendedOrganization } from "./types";

export const useOrganizationData = () => {
  const [organization, setOrganization] = useState<ExtendedOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (!user) {
        console.log("Aucun utilisateur connecté");
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        console.log("Récupération des données de l'organisation pour l'utilisateur:", user.id);
        
        const organizationId = localStorage.getItem('currentOrganizationId');
        console.log("ID de l'organisation trouvé dans localStorage:", organizationId);
        
        if (!organizationId) {
          console.log("Aucun ID d'organisation dans localStorage, utilisation de RPC");
          const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_organization_safe', {
            p_user_id: user.id
          });
          
          if (rpcError) {
            console.error("Erreur RPC lors de la récupération de l'organisation:", rpcError);
            toast({
              title: "Erreur",
              description: "Impossible de déterminer l'organisation active.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
          
          if (!rpcData || rpcData.length === 0) {
            console.error("Aucune organisation trouvée via RPC");
            toast({
              title: "Erreur",
              description: "Aucune organisation trouvée pour cet utilisateur.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
          
          const orgId = rpcData[0].id;
          console.log("Organisation trouvée via RPC, ID:", orgId);
          localStorage.setItem('currentOrganizationId', orgId);
          
          // Récupérer les détails de l'organisation avec une requête explicite pour tous les champs
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select(`
              id, 
              name, 
              owner_id, 
              plan, 
              trial_ends_at, 
              is_active, 
              max_users, 
              max_profiles, 
              current_users, 
              current_profiles, 
              created_at, 
              updated_at, 
              subscription_plan_id,
              subscription_status,
              next_billing_date,
              canceled_at
            `)
            .eq('id', orgId)
            .single();
            
          if (orgError) {
            console.error("Erreur lors de la récupération des détails:", orgError);
            toast({
              title: "Erreur",
              description: "Impossible de charger les détails de l'organisation.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
          
          console.log("Détails de l'organisation récupérés:", orgData);
          
          // Récupérer l'email du propriétaire
          const { data: ownerData } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', orgData.owner_id)
            .single();
              
          const extendedOrgData: ExtendedOrganization = {
            ...orgData,
            owner_email: ownerData?.email || user.email
          };
          
          console.log("Données étendues de l'organisation:", extendedOrgData);
          setOrganization(extendedOrgData);
          setIsLoading(false);
          return;
        }
        
        console.log("Récupération des détails pour l'organisation ID:", organizationId);
        
        // Récupérer directement depuis la table organizations avec une requête explicite pour tous les champs
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select(`
            id, 
            name, 
            owner_id, 
            plan, 
            trial_ends_at, 
            is_active, 
            max_users, 
            max_profiles, 
            current_users, 
            current_profiles, 
            created_at, 
            updated_at, 
            subscription_plan_id,
            subscription_status,
            next_billing_date,
            canceled_at
          `)
          .eq('id', organizationId)
          .single();
        
        if (orgError) {
          console.error("Erreur lors de la récupération des détails de l'organisation:", orgError);
          toast({
            title: "Erreur",
            description: "Impossible de charger les détails de l'organisation.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        
        console.log("Détails de l'organisation récupérés:", orgData);
        
        if (orgData) {
          const { data: ownerData } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', orgData.owner_id)
            .single();
            
          const extendedOrgData: ExtendedOrganization = {
            ...orgData,
            owner_email: ownerData?.email || user.email
          };
          
          console.log("Données étendues de l'organisation:", extendedOrgData);
          setOrganization(extendedOrgData);
        }
      } catch (error: any) {
        console.error("Erreur lors du chargement des détails de l'organisation:", error.message);
        toast({
          title: "Erreur",
          description: "Impossible de charger les détails de l'organisation.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrganizationDetails();
  }, [user, toast]);

  return { organization, isLoading, setOrganization };
};
